import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { ChevronDown, ChevronRight, Copy, FileText, MessageCircle, Plus, RefreshCw, Search, Trash2, Wand2 } from '@/components/icons';
import { nextRequirementKey, REQUIREMENT_PRIORITIES, type Requirement, type RequirementPriority, type RequirementSetView } from '@1qa/shared';
import { copyText } from '@/lib/format';
import { PRIORITY_LABEL, PRIORITY_TONE } from '@/lib/requirements';
import { Badge, Button, Card, CardHeader, Input, Select, Textarea, useToast } from '@/components/ui';


function RequirementRow({ r, canEdit, onChange, onRemove, coverage }: { r: Requirement; canEdit: boolean; onChange: (r: Requirement) => void; onRemove: () => void; coverage?: number }) {
  const [open, setOpen] = useState(false);
  return (
    <li className={clsx('px-4 py-3', !r.selected && 'opacity-60')} data-testid={`requirement-${r.key}`}>
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          className="mt-1 size-4 accent-[var(--color-primary)]"
          checked={r.selected}
          disabled={!canEdit}
          onChange={(e) => onChange({ ...r, selected: e.target.checked })}
          aria-label={`Write tests for ${r.key}`}
        />
        <button type="button" className="flex min-w-0 flex-1 items-start gap-2 text-left" onClick={() => setOpen(!open)} aria-expanded={open}>
          {open ? <ChevronDown className="mt-1 size-3 shrink-0 text-fg-muted" /> : <ChevronRight className="mt-1 size-3 shrink-0 text-fg-muted" />}
          <span className="min-w-0">
            <span className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs text-fg-muted">{r.key}</span>
              <span className="text-sm font-medium text-fg">{r.title}</span>
              <Badge tone={PRIORITY_TONE[r.priority]}>{PRIORITY_LABEL[r.priority]}</Badge>
              {r.manual && <Badge tone="info">Added by you</Badge>}
              {coverage !== undefined && coverage > 0 && <Badge tone="success">{coverage} {coverage === 1 ? 'test' : 'tests'}</Badge>}
            </span>
            {!open && (r.description || r.acceptance.length > 0) && (
              <span className="mt-0.5 line-clamp-1 block text-xs text-fg-secondary">
                {r.description || r.acceptance[0]}
                {r.acceptance.length > 0 && ` · ${r.acceptance.length} acceptance ${r.acceptance.length === 1 ? 'criterion' : 'criteria'}`}
              </span>
            )}
          </span>
        </button>
        {canEdit && (
          <button type="button" aria-label={`Remove ${r.key}`} className="mt-0.5 text-fg-muted hover:text-danger-text" onClick={onRemove}>
            <Trash2 className="size-3.5" />
          </button>
        )}
      </div>
      {open && (
        <div className="mt-3 ml-7 grid gap-3">
          {canEdit ? (
            <>
              <div className="grid gap-3 sm:grid-cols-[1fr_160px]">
                <Input aria-label="Title" value={r.title} maxLength={200} onChange={(e) => onChange({ ...r, title: e.target.value })} />
                <Select
                  aria-label="Priority"
                  value={r.priority}
                  onChange={(e) => onChange({ ...r, priority: e.target.value as RequirementPriority })}
                  options={REQUIREMENT_PRIORITIES.map((p) => ({ value: p, label: `${PRIORITY_LABEL[p]} priority` }))}
                />
              </div>
              <Textarea aria-label="Description" rows={2} value={r.description} maxLength={4000} placeholder="What the app must do" onChange={(e) => onChange({ ...r, description: e.target.value })} />
              <Textarea
                aria-label="Acceptance criteria, one per line"
                rows={Math.min(8, Math.max(3, r.acceptance.length + 1))}
                value={r.acceptance.join('\n')}
                placeholder="Acceptance criteria, one per line"
                onChange={(e) => onChange({ ...r, acceptance: e.target.value.split('\n').map((l) => l.trimStart()).filter((l, i, all) => l || i === all.length - 1).slice(0, 30) })}
              />
              <Input aria-label="Where in the document" value={r.source} maxLength={500} placeholder="Where in the document (section, quote)" onChange={(e) => onChange({ ...r, source: e.target.value })} />
            </>
          ) : (
            <>
              {r.description && <p className="text-sm text-fg-secondary">{r.description}</p>}
              {r.acceptance.length > 0 && (
                <ul className="list-disc space-y-1 pl-5 text-sm text-fg-secondary">
                  {r.acceptance.map((a) => (
                    <li key={a}>{a}</li>
                  ))}
                </ul>
              )}
            </>
          )}
          {r.source && !canEdit && <p className="text-xs text-fg-muted">Source: {r.source}</p>}
        </div>
      )}
    </li>
  );
}

/** Step 2: what the model found; people edit, add and remove requirements and pick which get tests. */
export function RequirementsStep({
  set,
  requirements,
  onChange,
  onAnalyze,
  onGenerate,
  analyzing,
  generating,
  coverage,
}: {
  set: RequirementSetView;
  requirements: Requirement[];
  onChange: (next: Requirement[]) => void;
  onAnalyze: () => void;
  onGenerate: () => void;
  analyzing: boolean;
  generating: boolean;
  coverage: Map<string, { drafts: number }>;
}) {
  const toast = useToast();
  const [query, setQuery] = useState('');
  const [priority, setPriority] = useState<'' | RequirementPriority>('');
  const [showText, setShowText] = useState(false);
  const canEdit = set.canEdit;
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return requirements.filter(
      (r) => (!priority || r.priority === priority) && (!q || `${r.key} ${r.title} ${r.description} ${r.acceptance.join(' ')}`.toLowerCase().includes(q)),
    );
  }, [requirements, query, priority]);
  const selected = requirements.filter((r) => r.selected).length;
  const uncoveredSelected = requirements.filter((r) => r.selected && !(coverage.get(r.key)?.drafts ?? 0)).length;
  const saved = set.counts.saved > 0;

  const update = (key: string, r: Requirement) => onChange(requirements.map((x) => (x.key === key ? r : x)));
  const addOne = () =>
    onChange([...requirements, { key: nextRequirementKey(requirements), title: 'New requirement', description: '', acceptance: [], priority: 'MEDIUM', source: '', selected: true, manual: true }]);
  const selectAll = (v: boolean) => onChange(requirements.map((r) => (shown.includes(r) ? { ...r, selected: v } : r)));

  if (!requirements.length) {
    return (
      <Card className="p-8 text-center">
        <Wand2 className="mx-auto size-7 text-primary" aria-hidden />
        <h2 className="mt-3 text-lg font-semibold text-fg">Find the requirements</h2>
        <p className="mx-auto mt-1 max-w-lg text-sm text-fg-secondary">
          The project’s AI model reads the documents ({set.chars.toLocaleString('en')} characters) and lists every testable requirement with its
          acceptance criteria and priority. Nothing is saved as a test yet.
        </p>
        {canEdit && (
          <Button className="mt-5" icon={<Wand2 className="size-4" />} loading={analyzing} onClick={onAnalyze} data-testid="requirements-analyze">
            Find requirements
          </Button>
        )}
      </Card>
    );
  }

  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
      <Card>
        <CardHeader
          title={`2. Requirements (${requirements.length})`}
          description="Check what the AI model found against the document. Edit, add or remove requirements and tick the ones that need tests."
          actions={
            canEdit && (
              <Button variant="secondary" size="sm" icon={<Plus className="size-3.5" />} onClick={addOne}>
                Add requirement
              </Button>
            )
          }
        />
        <div className="flex flex-wrap items-center gap-3 border-b border-border-subtle px-4 py-3">
          <div className="relative min-w-48 flex-1">
            <Search className="pointer-events-none absolute top-1/2 left-3 size-3.5 -translate-y-1/2 text-fg-muted" aria-hidden />
            <Input aria-label="Search requirements" className="pl-9" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search" />
          </div>
          <Select
            aria-label="Priority"
            wrapperClassName="w-40"
            value={priority}
            onChange={(e) => setPriority(e.target.value as '' | RequirementPriority)}
            options={[{ value: '', label: 'Any priority' }, ...REQUIREMENT_PRIORITIES.map((p) => ({ value: p, label: PRIORITY_LABEL[p] }))]}
          />
          {canEdit && (
            <span className="flex gap-2 text-xs">
              <button type="button" className="text-link hover:underline" onClick={() => selectAll(true)}>
                Tick all
              </button>
              <button type="button" className="text-link hover:underline" onClick={() => selectAll(false)}>
                Untick all
              </button>
            </span>
          )}
        </div>
        <ul className="divide-y divide-border-subtle">
          {shown.map((r) => (
            <RequirementRow
              key={r.key}
              r={r}
              canEdit={canEdit}
              coverage={coverage.get(r.key)?.drafts}
              onChange={(next) => update(r.key, next)}
              onRemove={() => onChange(requirements.filter((x) => x.key !== r.key))}
            />
          ))}
          {!shown.length && <li className="px-4 py-8 text-center text-sm text-fg-muted">No requirement matches.</li>}
        </ul>
        {canEdit && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border-subtle px-4 py-4">
            <span className="text-sm text-fg-secondary">
              {selected} of {requirements.length} ticked{uncoveredSelected < selected && ` · ${selected - uncoveredSelected} already have test cases`}
            </span>
            <Button icon={<Wand2 className="size-4" />} loading={generating} disabled={!uncoveredSelected} onClick={onGenerate} data-testid="requirements-generate">
              Write test cases for {uncoveredSelected} {uncoveredSelected === 1 ? 'requirement' : 'requirements'}
            </Button>
          </div>
        )}
      </Card>

      <div className="space-y-6">
        <Card>
          <CardHeader title="Questions for the business team" description="Where the document is unclear or missing details." />
          {set.questions.length ? (
            <>
              <ul className="space-y-2 px-5 py-4 text-sm text-fg-secondary">
                {set.questions.map((q) => (
                  <li key={q} className="flex gap-2">
                    <MessageCircle className="mt-0.5 size-3.5 shrink-0 text-primary" aria-hidden />
                    {q}
                  </li>
                ))}
              </ul>
              <div className="border-t border-border-subtle px-5 py-3">
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<Copy className="size-3.5" />}
                  onClick={async () => (await copyText(set.questions.map((q, i) => `${i + 1}. ${q}`).join('\n'))) && toast.success('Questions copied')}
                >
                  Copy questions
                </Button>
              </div>
            </>
          ) : (
            <p className="px-5 py-4 text-sm text-fg-muted">None: the document looks complete to the model.</p>
          )}
        </Card>

        <Card>
          <CardHeader title="Documents" />
          <ul className="space-y-2 px-5 py-4 text-sm">
            {set.sources.map((s) => (
              <li key={s.name}>
                <span className="flex items-center gap-2 text-fg">
                  <FileText className="size-3.5 text-fg-muted" aria-hidden />
                  <span className="truncate">{s.name}</span>
                  {s.chars > 0 && <span className="ml-auto shrink-0 text-xs text-fg-muted">{s.chars.toLocaleString('en')} chars</span>}
                </span>
                {s.note && <span className="mt-0.5 block text-xs text-warning">{s.note}</span>}
              </li>
            ))}
          </ul>
          {set.notes && <p className="border-t border-border-subtle px-5 py-3 text-xs text-fg-secondary">Notes: {set.notes}</p>}
          <div className="border-t border-border-subtle px-5 py-3">
            <button type="button" className="text-xs text-link hover:underline" onClick={() => setShowText(!showText)}>
              {showText ? 'Hide the text 1QA read' : 'Show the text 1QA read'}
            </button>
            {showText && <pre className="mt-2 max-h-80 overflow-auto rounded-field bg-surface-sunken p-3 text-xs whitespace-pre-wrap text-fg-secondary">{set.preview}{set.chars > set.preview.length && '\n…'}</pre>}
          </div>
          {canEdit && !saved && (
            <div className="border-t border-border-subtle px-5 py-3">
              <Button variant="ghost" size="sm" icon={<RefreshCw className="size-3.5" />} loading={analyzing} onClick={onAnalyze}>
                Find requirements again
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
